import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import { forkJoin, Observable, Subscription } from 'rxjs';
import { delay } from 'rxjs/operators';
import { LoadingService } from '../services/loading.service';
import { ConfirmDialogComponent, ConfirmDialogModel } from '../helpers/confirm-dialog/confirm-dialog.component';
import { Extensions } from '../helpers/extensions';
import { Authentication } from '../models/authentication';
import { Post } from '../models/post';
import { Event } from '../models/event';
import { MessageService } from '../services/message.service';
import { NotificationService } from '../services/notification.service';
import { SignalrService } from '../services/signalr.service';
import { UserService } from '../services/user.service';
import { AddPostComponent } from './add-post/add-post.component';
import { PostService } from '../services/post.service';
import { Permissions, Role, Roles } from '../models/role';
import { AddPollComponent } from './add-poll/add-poll.component';
import { Poll } from '../models/poll';
import { PollOption } from '../models/poll-option';
import { ViewPollVotesComponent } from './view-poll-votes/view-poll-votes.component';
import { EventService } from '../services/event.service';
import { AddEventComponent } from './add-event/add-event.component';
import { EventPresenceListComponent } from './event-presence-list/event-presence-list.component';

@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss']
})
export class HomeComponent implements OnInit {
  posts = new MatTableDataSource<Post | Poll | Event>();
  loggedUser: Authentication;
  unreadChats: number;
  messageSubscription: Subscription;
  allowPostManagement: boolean;
  allowEditingAnyPost: boolean;
  allowMessagesAccess: boolean;
  chips = [
    { label: 'Todos', value: PostFilters.All, target: PostFilterTargets.All, selected: true },
    { label: 'Não vistas', value: PostFilters.NotSeen, target: PostFilterTargets.All, selected: false },
    { label: 'Somente para mim', value: PostFilters.HasTargetUser, target: PostFilterTargets.Associates, selected: false },
    { label: 'Curtidas', value: PostFilters.Liked, target: PostFilterTargets.All, selected: false },
    { label: 'Não curtidas', value: PostFilters.NotLiked, target: PostFilterTargets.All, selected: false },
    { label: 'Imagens', value: PostFilters.Images, target: PostFilterTargets.All, selected: false },
    { label: 'Vídeos', value: PostFilters.Videos, target: PostFilterTargets.All, selected: false },
    { label: 'Enquetes', value: PostFilters.Polls, target: PostFilterTargets.All, selected: false },
    { label: 'Eventos', value: PostFilters.Events, target: PostFilterTargets.All, selected: false },
  ];

  upcomingEvents: Event[] = [];
  openPolls: Poll[] = [];
  // Most used pages, shown in the feed's left column to those allowed to open them. `associates` mirrors
  // the route's ignoreRolesWhenAssociate, which lets associates in regardless of roles
  shortcuts = [
    { title: 'Notas de Safra', icon: 'newspaper', route: '/reports/invoices', permission: Permissions.ViewInvoices, associates: true },
    { title: 'Extrato de Safra', icon: 'receipt_long', route: '/reports/harvest-statements', permission: Permissions.ViewFinance, associates: true },
    { title: 'Financeiro', icon: 'attach_money', route: '/reports/financial-transactions', permission: Permissions.ViewFinance, associates: true },
    { title: 'Produtividade', icon: 'trending_up', route: '/reports/productivity-report', permission: Permissions.ViewHarvestReports, associates: false },
    { title: 'Publicações Legais', icon: 'history_edu', route: '/documents/legal-posts', permission: null, associates: true },
  ];

  // Widths of the placeholder cards' titles shown while the feed loads
  skeletonWidths = [55, 40, 65];
  loading$: Observable<boolean>;

  @ViewChild('searchInput') searchInput: ElementRef<HTMLInputElement>;

  constructor(private postService: PostService, private notificationService: NotificationService, private userService: UserService,
    private dialog: MatDialog, private messageService: MessageService, private router: Router, public signalRService: SignalrService,
    private eventService: EventService, loadingService: LoadingService) {
    // delay(0) avoids an ExpressionChangedAfterItHasBeenCheckedError, as in AppComponent
    this.loading$ = loadingService.loadingSub.pipe(delay(0));
  }

  ngOnInit() {
    this.loggedUser = this.userService.authenticationValue;
    this.allowPostManagement = Extensions.isUserInAnyRole(this.loggedUser, Role.getAllowedRoles(Permissions.ManagePosts));
    this.allowMessagesAccess = this.loggedUser.isAssociate || Extensions.isUserInAnyRole(this.loggedUser, Role.getAllowedRoles(Permissions.AccessMessages));
    this.allowEditingAnyPost = Extensions.isUserInRole(this.loggedUser, Roles.Admin);
    // Same rule as AuthGuard
    this.shortcuts = this.shortcuts.filter(s => s.permission == null || (s.associates && this.loggedUser.isAssociate)
      || Extensions.isUserInAnyRole(this.loggedUser, Role.getAllowedRoles(s.permission)));

    this.chips = this.chips.filter(c => {
      if (c.target == PostFilterTargets.All) {
        return c;
      }
      else if (c.target == PostFilterTargets.Associates && this.loggedUser?.isAssociate) {
        return c;
      }
      else if (c.target == PostFilterTargets.Admins && !this.loggedUser?.isAssociate) {
        return c;
      }
    });

    this.messageSubscription = this.signalRService.messageSubscription.subscribe({
      next: subscription => {
        if (subscription) {
          this.messageService.getChats().subscribe({
            next: chats => {
              this.unreadChats = chats.filter(c => c.unread).length;
            },
            error: error => {
              this.notificationService.error(error);
            }
          });
        }
      }
    });

    this.getPosts();
    this.messageService.getChats().subscribe({
      next: chats => {
        this.unreadChats = chats.filter(c => c.unread).length;
      },
      error: error => {
        this.notificationService.error(error);
      }
    });
  }

  ngOnDestroy() {
    this.messageSubscription.unsubscribe();
  }

  getPosts() {
    forkJoin([this.postService.getPosts(), this.postService.getPolls(), this.eventService.getEvents({ includePastEvents: true })]).subscribe({
      next: data => {
        this.posts.data = [...data[0].map(x => new Post(x)), ...data[1].map(x => new Poll(x)), ...data[2].map(x => new Event(x))];
        this.posts.data.sort((a, b) => new Date((a instanceof Event) ? a.date : a.postDate) >= new Date((b instanceof Event) ? b.date : b.postDate) ? -1 : 1);

        const now = new Date();
        this.upcomingEvents = this.posts.data
          .filter(p => p instanceof Event && !p.wasCancelled && new Date(p.date) >= now)
          .map(p => p as Event)
          .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
          .slice(0, 3);
        this.openPolls = this.posts.data
          .filter(p => p instanceof Poll && p.allowVoting && !p.hasVoted && !p.isExpired)
          .map(p => p as Poll)
          .sort((a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime())
          .slice(0, 5);
      },
      error: error => {
        this.notificationService.error(error);
      }
    });
  }

  updatePostVisibility(post: Post, isVisible: boolean) {
    if (isVisible && post.unread) {
      this.readPost(post);
    }
  }

  applyFilter = (value: string) => {
    let selectedChip = this.chips.find(c => c.selected);

    if (selectedChip != null) {
      this.selectFilter(selectedChip)
    }
  }

  selectFilter(chip: any) {
    this.chips.forEach(c => {
      c.selected = c == chip;
    });

    var posts = this.posts;
    posts.filter = this.searchInput.nativeElement?.value?.trim().toLocaleLowerCase();
    this.posts.filteredData = posts.filteredData.filter(p => this.matchesFilter(p, chip.value));
  }

  // Items of the whole feed (search aside) each filter would show, for the counts in the filters list
  countFor(chip: any): number {
    return this.posts.data.filter(p => this.matchesFilter(p, chip.value)).length;
  }

  private matchesFilter(post: Post | Poll | Event, filter: PostFilters): boolean {
    switch (filter) {
      case PostFilters.All:
        return true;
      case PostFilters.NotSeen:
        return (post instanceof Post) && post.unread;
      case PostFilters.HasTargetUser:
        return (post instanceof Post) && post.hasTargetUser;
      case PostFilters.Liked:
        return (post instanceof Post) && post.isLiked;
      case PostFilters.NotLiked:
        return (post instanceof Post) && !post.isLiked;
      case PostFilters.Images:
        return (post instanceof Post) && post.attachments.filter(a => a.fileType.startsWith('image')).length > 0;
      case PostFilters.Videos:
        return (post instanceof Post) && post.attachments.filter(a => a.fileType.startsWith('video')).length > 0;
      case PostFilters.Polls:
        return post instanceof Poll;
      case PostFilters.Events:
        return post instanceof Event;
    }
  }

  // Shows every item again and scrolls to the given card, briefly highlighting it
  focusItem(item: Poll | Event) {
    this.searchInput.nativeElement.value = '';
    this.selectFilter(this.chips[0]);

    setTimeout(() => {
      const element = document.getElementById(this.getItemID(item));
      element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      element?.classList.add('feed-highlight');
      setTimeout(() => element?.classList.remove('feed-highlight'), 1600);
    });
  }

  getItemID(item: Post | Poll | Event): string {
    return `${item instanceof Event ? 'event' : item instanceof Poll ? 'poll' : 'post'}-${item.id}`;
  }

  ratePost(post: Post) {
    this.postService.ratePost(post.id, { postID: post.id, isLiked: !post.isLiked }).subscribe({
      next: () => {
        post.isLiked = !post.isLiked;
        post.likedByCount += post.isLiked ? 1 : -1;
      },
      error: error => {
        this.notificationService.error(error);
      }
    });
  }

  addPost() {
    const dialogRef = this.dialog.open(AddPostComponent, { panelClass: "dialog-extra-large", data: { isStatic: false } });

    dialogRef.afterClosed().subscribe({
      next: post => {
        if (post) {
          this.postService.createPost(post).subscribe({
            next: () => {
              this.notificationService.sucess('Publicação adicionada.');
              this.getPosts();
            },
            error: error => {
              this.notificationService.error(error);
            }
          });
        }
      }
    });
  }

  editPost(post: Post) {
    const dialogRef = this.dialog.open(AddPostComponent, {
      panelClass: "dialog-large", data: { post: post, isStatic: false }
    });

    dialogRef.afterClosed().subscribe({
      next: editedPost => {
        if (editedPost) {
          editedPost.ID = post.id;
          this.postService.updatePost(post.id, editedPost).subscribe({
            next: () => {
              this.notificationService.sucess('Publicação atualizada.');
              this.getPosts();
            }
          });
        }
      }
    });
  }

  deletePost(post: Post) {
    const message = `Deseja realmente excluir a publicação <strong>${post.title}</strong>?`;
    const dialogData = new ConfirmDialogModel("Exclusão", message);
    const dialogRef = this.dialog.open(ConfirmDialogComponent, { data: dialogData });

    dialogRef.afterClosed().subscribe(
      result => {
        if (result) {
          this.postService.deletePost(post.id).subscribe({
            next: () => {
              this.notificationService.sucess("Publicação excluída.");
              this.getPosts();
            },
            error: error => {
              this.notificationService.error(error);
            }
          });
        }
      }
    );
  }

  readPost(post: Post) {
    this.postService.readPost(post.id, { postID: post.id }).subscribe({
      next: () => {
        post.unread = false;
        post.readByCount++;
      },
      error: error => {
        this.notificationService.error(error);
      }
    });
  }

  addPoll() {
    const dialogRef = this.dialog.open(AddPollComponent, { panelClass: "dialog-large" });

    dialogRef.afterClosed().subscribe({
      next: poll => {
        if (poll) {
          this.postService.createPoll(poll).subscribe({
            next: () => {
              this.notificationService.sucess('Enquete adicionada.');
              this.getPosts();
            },
            error: error => {
              this.notificationService.error(error);
            }
          });
        }
      }
    });
  }

  deletePoll(poll: Poll) {
    const message = `Deseja realmente excluir a enquete <strong>${poll.title}</strong>?`;
    const dialogData = new ConfirmDialogModel("Exclusão", message);
    const dialogRef = this.dialog.open(ConfirmDialogComponent, { data: dialogData });

    dialogRef.afterClosed().subscribe(
      result => {
        if (result) {
          this.postService.deletePoll(poll.id).subscribe({
            next: () => {
              this.notificationService.sucess("Enquete excluída.");
              this.getPosts();
            },
            error: error => {
              this.notificationService.error(error);
            }
          });
        }
      }
    );
  }

  voteInPoll(poll: Poll, option: PollOption) {
    this.postService.voteInPoll({ pollID: poll.id, optionID: option.id }).subscribe({
      next: () => {
        this.notificationService.sucess("Voto confirmado.");
        this.getPosts();
      },
      error: error => {
        this.notificationService.error(error);
      }
    });
  }

  viewPollVotes(poll: Poll) {
    this.dialog.open(ViewPollVotesComponent, { panelClass: "dialog-extra-large", data: { poll: poll } });
  }

  addEvent() {
    const dialogRef = this.dialog.open(AddEventComponent, { panelClass: "dialog-extra-large" });

    dialogRef.afterClosed().subscribe({
      next: post => {
        if (post) {
          this.eventService.createEvent(post).subscribe({
            next: () => {
              this.notificationService.sucess('Evento criado.');
              this.getPosts();
            },
            error: error => {
              this.notificationService.error(error);
            }
          });
        }
      }
    });
  }

  editEvent(event: Event) {
    const dialogRef = this.dialog.open(AddEventComponent, {
      panelClass: "dialog-extra-large", data: { event: event }
    });

    dialogRef.afterClosed().subscribe({
      next: editedEvent => {
        if (editedEvent) {
          editedEvent.ID = event.id;
          this.eventService.updateEvent(event.id, editedEvent).subscribe({
            next: () => {
              this.notificationService.sucess('Evento atualizado.');
              this.getPosts();
            }
          });
        }
      }
    });
  }

  deleteEvent(event: Event) {
    const message = `Deseja realmente cancelar o evento <strong>${event.title}</strong>?`;
    const dialogData = new ConfirmDialogModel("Cancelar", message);
    const dialogRef = this.dialog.open(ConfirmDialogComponent, { data: dialogData });

    dialogRef.afterClosed().subscribe(
      result => {
        if (result) {
          this.eventService.cancelEvent(event.id).subscribe({
            next: () => {
              this.notificationService.sucess('Evento cancelado.');
              this.getPosts();
            },
            error: error => {
              this.notificationService.error(error);
            }
          });
        }
      }
    );
  }

  viewEventPresenceList(event: Event) {
    this.dialog.open(EventPresenceListComponent, {
      panelClass: "dialog-extra-large", data: { eventID: event.id }
    });
  }

  goToMessages() {
    this.router.navigate(['/messaging']);
  }

  getPostDescription(post: Post) {
    return post.description?.replace(/\n/g, '<br>');
  }

  isFileImage(fileType: string) {
    switch (fileType) {
      case 'image/png':
        return true;
      case 'image/jpeg':
        return true;
      case 'image/jpg':
        return true;
      default:
        return false;
    }
  }

  isPost(post: Post | Poll | Event): boolean {
    return post instanceof Post;
  }

  isPoll(post: Post | Poll | Event): boolean {
    return post instanceof Poll;
  }

  isEvent(post: Post | Poll | Event): boolean {
    return post instanceof Event;
  }
}

enum PostFilters {
  All,
  NotSeen,
  HasTargetUser,
  Liked,
  NotLiked,
  Images,
  Videos,
  Polls,
  Events,
}

enum PostFilterTargets {
  All,
  Admins,
  Associates
}
